import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Minus, Undo, Redo,
  Eye, EyeOff, Printer, Save, ArrowLeft, Palette,
  Highlighter, Link2, ImagePlus, Type, ChevronDown, Table2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  STANDARD_TEMPLATES, TEMPLATE_CATEGORIES,
  resolveVariables, fillTemplate, buildPrintDocument,
} from '@/lib/documentTemplates';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { selectCompany } from '@/store/slices/companySlice';
import { selectUser } from '@/store/slices/authSlice';
import { toast } from 'sonner';

// ─── Variable groups (for sidebar) ────────────────────────────────────────────

const VAR_GROUPS = [
  {
    label: 'Company',
    vars: [
      { key: 'company_name',     label: 'Company Name' },
      { key: 'company_address',  label: 'Company Address' },
      { key: 'company_phone',    label: 'Company Phone' },
      { key: 'company_email',    label: 'Company Email' },
    ],
  },
  {
    label: 'Employee',
    vars: [
      { key: 'employee_name',    label: 'Employee Name' },
      { key: 'employee_code',    label: 'Employee Code' },
      { key: 'designation',      label: 'Designation' },
      { key: 'department',       label: 'Department' },
      { key: 'date_of_joining',  label: 'Date of Joining' },
      { key: 'date_of_birth',    label: 'Date of Birth' },
      { key: 'employee_address', label: 'Employee Address' },
      { key: 'employment_type',  label: 'Employment Type' },
      { key: 'years_of_service', label: 'Years of Service' },
      { key: 'manager_name',     label: 'Manager Name' },
    ],
  },
  {
    label: 'Salary',
    vars: [
      { key: 'gross_salary',       label: 'Gross Salary' },
      { key: 'basic_salary',       label: 'Basic Salary' },
      { key: 'annual_ctc',         label: 'Annual CTC' },
      { key: 'revised_salary',     label: 'Revised Salary' },
      { key: 'increment_amount',   label: 'Increment Amount' },
      { key: 'increment_percent',  label: 'Increment %' },
    ],
  },
  {
    label: 'Dates & Reference',
    vars: [
      { key: 'document_date',     label: 'Document Date' },
      { key: 'document_year',     label: 'Document Year' },
      { key: 'effective_date',    label: 'Effective Date' },
      { key: 'last_working_day',  label: 'Last Working Day' },
      { key: 'confirmation_date', label: 'Confirmation Date' },
      { key: 'reply_by_date',     label: 'Reply By Date' },
      { key: 'reference_no',      label: 'Reference No.' },
    ],
  },
  {
    label: 'HR & Policy',
    vars: [
      { key: 'hr_signatory',    label: 'HR Signatory' },
      { key: 'hr_designation',  label: 'HR Designation' },
      { key: 'notice_period',   label: 'Notice Period' },
      { key: 'probation_period',label: 'Probation Period' },
      { key: 'new_designation', label: 'New Designation' },
      { key: 'new_department',  label: 'New Department' },
      { key: 'new_location',    label: 'New Location' },
    ],
  },
  {
    label: 'Other',
    vars: [
      { key: 'warning_subject',    label: 'Warning Subject' },
      { key: 'warning_incident',   label: 'Warning Incident' },
      { key: 'termination_reason', label: 'Termination Reason' },
      { key: 'loan_amount',        label: 'Loan Amount' },
      { key: 'loan_emi',           label: 'Loan EMI' },
    ],
  },
];

const SAMPLE_EMPLOYEE = {
  first_name: 'Rahul', last_name: 'Sharma',
  employee_code: 'EMP-00001',
  designation: { name: 'Senior Software Engineer' },
  department: { name: 'Engineering' },
  joining_date: '2022-01-15',
  salary: { gross_monthly: 75000, basic: 35000 },
  reporting_manager: { name: 'Priya Mehta', designation: 'Engineering Manager' },
};

// ─── Table size picker ────────────────────────────────────────────────────────

function TablePicker({ onSelect, onClose }) {
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const ROWS = 6, COLS = 6;

  return (
    <div className="absolute top-full left-0 z-50 mt-1 p-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[12px] shadow-[var(--shadow-lg)]">
      <p className="text-[10px] text-[var(--text-tertiary)] mb-2 text-center font-medium">
        {hover.r > 0 ? `${hover.r} × ${hover.c} Table` : 'Hover to pick size'}
      </p>
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${COLS}, 1.3rem)` }}>
        {Array.from({ length: ROWS * COLS }).map((_, i) => {
          const r = Math.floor(i / COLS) + 1;
          const c = (i % COLS) + 1;
          const active = r <= hover.r && c <= hover.c;
          return (
            <div key={i}
              onMouseEnter={() => setHover({ r, c })}
              onMouseLeave={() => setHover({ r: 0, c: 0 })}
              onClick={() => { onSelect(r, c); onClose(); }}
              className={cn(
                'w-[1.1rem] h-[1.1rem] rounded-[3px] border cursor-pointer transition-all',
                active
                  ? 'border-[var(--accent)] bg-[var(--accent-muted)]'
                  : 'border-[var(--border)] hover:border-[var(--accent)]/50',
              )}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Toolbar button ───────────────────────────────────────────────────────────

function TBtn({ onClick, title, active, children }) {
  return (
    <button type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className={cn(
        'w-7 h-7 flex items-center justify-center rounded-[6px] transition-all',
        'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
        active && 'bg-[var(--accent-muted)] text-[var(--accent)]',
      )}>
      {children}
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DocumentBuilderPage() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isEdit    = !!id;
  const company   = useSelector(selectCompany);
  const user      = useSelector(selectUser);
  const { data: settingsData } = useCompanySettings();
  const { mutate: saveSettings, isPending } = useUpdateCompanySettings();

  const editorRef  = useRef(null);
  const savedRange = useRef(null);
  const colorRef   = useRef(null);
  const hlRef      = useRef(null);

  const [name,        setName]     = useState('');
  const [category,    setCategory] = useState('ONBOARDING');
  const [desc,        setDesc]     = useState('');
  const [preview,     setPreview]  = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showTable,   setShowTable]   = useState(false);
  const [openGroup,   setOpenGroup]   = useState('Employee');

  // Load template for edit
  useEffect(() => {
    if (!isEdit) return;
    const customs = settingsData?.data?.settings?.document_templates || [];
    const all = [...STANDARD_TEMPLATES, ...customs];
    const tpl = all.find(t => t.id === id);
    if (!tpl) return;
    setName(tpl.name);
    setCategory(tpl.category);
    setDesc(tpl.description || '');
    if (editorRef.current) editorRef.current.innerHTML = tpl.content_html || '';
  }, [id, isEdit, settingsData]);

  // Save cursor position (only when inside editor)
  const saveCursor = useCallback(() => {
    const sel = window.getSelection();
    if (sel?.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        savedRange.current = range.cloneRange();
      }
    }
  }, []);

  // Restore and focus
  const restoreCursor = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    if (savedRange.current) {
      try {
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(savedRange.current);
      } catch { /* stale range */ }
    }
  }, []);

  // Formatting wrapper
  const exec = useCallback((cmd, val = null) => {
    restoreCursor();
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    saveCursor();
  }, [restoreCursor, saveCursor]);

  // Variable insertion — no contenteditable="false", fully editable span
  const insertVariable = useCallback((key) => {
    const editor = editorRef.current;
    if (!editor) return;
    restoreCursor();

    const sel = window.getSelection();
    let range;
    if (sel?.rangeCount > 0) {
      range = sel.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }
    } else {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    const span = document.createElement('span');
    span.style.cssText = 'background:#eff6ff;color:#1d4ed8;border-radius:3px;padding:1px 5px;font-family:ui-monospace,monospace;font-size:0.87em;white-space:nowrap;';
    span.setAttribute('data-var', key);
    span.textContent = `{{${key}}}`;

    const space = document.createTextNode(' '); // nbsp after span

    range.deleteContents();
    range.insertNode(space);
    range.insertNode(span);

    range.setStartAfter(space);
    range.collapse(true);
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
    savedRange.current = range.cloneRange();
  }, [restoreCursor]);

  // Table insertion
  const insertTable = useCallback((rows, cols) => {
    restoreCursor();
    let html = '<br><table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;">';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        if (r === 0) {
          html += `<th style="border:1.5px solid #94a3b8;background:#f1f5f9;padding:8px 12px;text-align:left;font-weight:600;color:#334155;">Column ${c + 1}</th>`;
        } else {
          html += '<td style="border:1px solid #cbd5e1;padding:8px 12px;color:#1e293b;min-width:80px;">&nbsp;</td>';
        }
      }
      html += '</tr>';
    }
    html += '</table><br>';
    document.execCommand('insertHTML', false, html);
    editorRef.current?.focus();
    saveCursor();
  }, [restoreCursor, saveCursor]);

  // Link insertion
  const insertLink = useCallback(() => {
    restoreCursor();
    const sel = window.getSelection();
    const selectedText = sel?.toString() || '';
    const url = prompt('Enter URL (e.g. https://example.com):');
    if (!url) return;
    exec('insertHTML', `<a href="${url}" style="color:#1d4ed8;text-decoration:underline;">${selectedText || url}</a>`);
  }, [restoreCursor, exec]);

  // Image (base64, Phase 1)
  const insertImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        restoreCursor();
        exec('insertHTML', `<img src="${ev.target.result}" alt="" style="max-width:100%;height:auto;display:block;margin:8px 0;" />`);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [restoreCursor, exec]);

  // Preview with sample data fill
  const togglePreview = () => {
    if (!preview) {
      const html = editorRef.current?.innerHTML || '';
      const vars = resolveVariables(company, SAMPLE_EMPLOYEE, user);
      setPreviewHtml(fillTemplate(html, vars));
    }
    setPreview(v => !v);
  };

  const handlePrintPreview = () => {
    const html   = editorRef.current?.innerHTML || '';
    const vars   = resolveVariables(company, SAMPLE_EMPLOYEE, user);
    const filled = fillTemplate(html, vars);
    const win    = window.open('', '_blank');
    if (!win) { toast.error('Allow popups for print preview'); return; }
    win.document.write(buildPrintDocument(filled, name || 'Template Preview'));
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const handleSave = () => {
    if (!name.trim()) { toast.error('Enter a template name'); return; }
    const content_html = editorRef.current?.innerHTML || '';
    if (!content_html.replace(/<[^>]+>/g, '').trim()) {
      toast.error('Template content cannot be empty'); return;
    }
    const customs = settingsData?.data?.settings?.document_templates || [];
    const newTpl = {
      id:          isEdit ? id : `tpl-custom-${Date.now()}`,
      name:        name.trim(),
      category,
      description: desc.trim(),
      is_standard: false,
      variables:   [],
      content_html,
    };
    const updated = isEdit
      ? customs.map(t => t.id === id ? newTpl : t)
      : [...customs, newTpl];
    saveSettings({ settings: { document_templates: updated } }, {
      onSuccess: () => { toast.success(`"${newTpl.name}" saved`); navigate('/documents/templates'); },
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-card)] shrink-0 flex-wrap">
        <button onClick={() => navigate('/documents/templates')}
          className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] shrink-0">
          <ArrowLeft size={15} />
        </button>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Template name…"
          className="flex-1 min-w-[180px] max-w-xs h-8 px-3 text-[13px] font-semibold bg-[var(--bg-app)] border border-[var(--border)] rounded-[8px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)]" />
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="h-8 px-2 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-[8px] text-[var(--text-secondary)] focus:outline-none">
          {TEMPLATE_CATEGORIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <div className="flex items-center gap-1.5 ml-auto">
          <button onClick={togglePreview}
            className={cn('flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium rounded-[8px] border transition-all',
              preview
                ? 'bg-[var(--accent-muted)] text-[var(--accent)] border-[var(--accent)]/40'
                : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
            {preview ? <EyeOff size={13}/> : <Eye size={13}/>}
            {preview ? 'Edit Mode' : 'Preview'}
          </button>
          <button onClick={handlePrintPreview}
            className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[8px] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-all">
            <Printer size={13}/> Print Preview
          </button>
          <button onClick={handleSave} disabled={isPending}
            className="flex items-center gap-1.5 h-8 px-4 text-[12px] font-semibold bg-[var(--accent)] text-white rounded-[8px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-all">
            <Save size={13}/> {isPending ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>

      {preview ? (
        /* ── Preview Mode ──────────────────────────────────────────────── */
        <div className="flex-1 overflow-auto bg-[var(--bg-app)] p-6">
          <div className="max-w-[760px] mx-auto">
            <div className="flex items-center gap-2 px-4 py-2 mb-4 rounded-[10px] bg-amber-50 border border-amber-200 text-[12px] text-amber-700">
              <Eye size={13}/>
              Preview uses sample data (Rahul Sharma, Engineering). Real data fills when you issue the document.
            </div>
            <div className="bg-white shadow-md rounded-[10px] overflow-hidden p-12"
              dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      ) : (
        /* ── Edit Mode ─────────────────────────────────────────────────── */
        <div className="flex flex-1 overflow-hidden">

          {/* Variables Sidebar */}
          <div className="w-56 border-r border-[var(--border)] bg-[var(--bg-card)] flex flex-col shrink-0 overflow-hidden">
            <div className="px-3 pt-3 pb-2 border-b border-[var(--border)]">
              <p className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-widest mb-0.5">Variables</p>
              <p className="text-[10px] text-[var(--text-tertiary)] leading-snug">Click to insert at cursor position</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {VAR_GROUPS.map(group => (
                <div key={group.label} className="border-b border-[var(--border)]">
                  <button
                    type="button"
                    onClick={() => setOpenGroup(g => g === group.label ? null : group.label)}
                    className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-all">
                    {group.label}
                    <ChevronDown size={11} className={cn('transition-transform duration-150', openGroup === group.label && 'rotate-180')} />
                  </button>
                  {openGroup === group.label && (
                    <div className="pb-1">
                      {group.vars.map(v => (
                        <button key={v.key} type="button"
                          onMouseDown={e => { e.preventDefault(); saveCursor(); insertVariable(v.key); }}
                          className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-[var(--accent-muted)] transition-all group text-left">
                          <span className="text-[10px] text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] truncate">{v.label}</span>
                          <span className="text-[9px] font-mono text-[var(--text-tertiary)] group-hover:text-[var(--accent)] ml-1 shrink-0 opacity-70">{`{{${v.key}}}`}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-3 py-3 border-t border-[var(--border)]">
              <label className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-1.5 block">Description</label>
              <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2}
                placeholder="Brief description…"
                className="w-full px-2 py-1.5 text-[11px] bg-[var(--bg-app)] border border-[var(--border)] rounded-[6px] text-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent)] resize-none" />
            </div>
          </div>

          {/* Editor Panel */}
          <div className="flex-1 flex flex-col overflow-hidden">

            {/* Formatting Toolbar */}
            <div className="shrink-0 px-2 py-1.5 border-b border-[var(--border)] bg-[var(--bg-card)] flex items-center gap-0.5 flex-wrap">

              {/* Block style */}
              <select
                onChange={e => { if (e.target.value) { restoreCursor(); exec('formatBlock', e.target.value); e.target.value = ''; } }}
                className="h-7 px-2 text-[11px] bg-[var(--bg-app)] border border-[var(--border)] rounded-[6px] text-[var(--text-secondary)] focus:outline-none cursor-pointer">
                <option value="">Style…</option>
                <option value="p">Paragraph</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="blockquote">Quote</option>
                <option value="pre">Code</option>
              </select>

              {/* Font size */}
              <select
                onChange={e => { if (e.target.value) { restoreCursor(); exec('fontSize', e.target.value); e.target.value = ''; } }}
                className="h-7 px-2 text-[11px] bg-[var(--bg-app)] border border-[var(--border)] rounded-[6px] text-[var(--text-secondary)] focus:outline-none cursor-pointer ml-1">
                <option value="">Size…</option>
                <option value="1">Tiny (8pt)</option>
                <option value="2">Small (10pt)</option>
                <option value="3">Normal (12pt)</option>
                <option value="4">Medium (14pt)</option>
                <option value="5">Large (18pt)</option>
                <option value="6">X-Large (24pt)</option>
                <option value="7">Huge (36pt)</option>
              </select>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              {/* Text decoration */}
              <TBtn onClick={() => exec('bold')}          title="Bold (Ctrl+B)">         <Bold          size={12}/></TBtn>
              <TBtn onClick={() => exec('italic')}        title="Italic (Ctrl+I)">       <Italic        size={12}/></TBtn>
              <TBtn onClick={() => exec('underline')}     title="Underline (Ctrl+U)">    <Underline     size={12}/></TBtn>
              <TBtn onClick={() => exec('strikeThrough')} title="Strikethrough">         <Strikethrough size={12}/></TBtn>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              {/* Colors */}
              <label title="Text Color" className="w-7 h-7 flex items-center justify-center rounded-[6px] cursor-pointer hover:bg-[var(--bg-hover)] transition-all text-[var(--text-secondary)]">
                <Palette size={12}/>
                <input ref={colorRef} type="color" defaultValue="#000000" className="sr-only"
                  onInput={e => { restoreCursor(); exec('foreColor', e.target.value); }} />
              </label>
              <label title="Highlight" className="w-7 h-7 flex items-center justify-center rounded-[6px] cursor-pointer hover:bg-[var(--bg-hover)] transition-all text-[var(--text-secondary)]">
                <Highlighter size={12}/>
                <input ref={hlRef} type="color" defaultValue="#ffff00" className="sr-only"
                  onInput={e => { restoreCursor(); exec('hiliteColor', e.target.value); }} />
              </label>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              {/* Alignment */}
              <TBtn onClick={() => exec('justifyLeft')}   title="Left"><AlignLeft    size={12}/></TBtn>
              <TBtn onClick={() => exec('justifyCenter')} title="Center"><AlignCenter  size={12}/></TBtn>
              <TBtn onClick={() => exec('justifyRight')}  title="Right"><AlignRight   size={12}/></TBtn>
              <TBtn onClick={() => exec('justifyFull')}   title="Justify"><AlignJustify size={12}/></TBtn>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              {/* Lists */}
              <TBtn onClick={() => exec('insertUnorderedList')} title="Bullet List">   <List        size={12}/></TBtn>
              <TBtn onClick={() => exec('insertOrderedList')}   title="Numbered List"> <ListOrdered size={12}/></TBtn>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              {/* Inserts */}
              <TBtn onClick={() => exec('insertHorizontalRule')} title="Divider Line"><Minus size={12}/></TBtn>

              {/* Table picker */}
              <div className="relative">
                <TBtn onClick={() => setShowTable(v => !v)} title="Insert Table" active={showTable}>
                  <Table2 size={12}/>
                </TBtn>
                {showTable && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowTable(false)} />
                    <div className="relative z-50">
                      <TablePicker onSelect={insertTable} onClose={() => setShowTable(false)} />
                    </div>
                  </>
                )}
              </div>

              <TBtn onClick={insertLink}  title="Insert Link">  <Link2      size={12}/></TBtn>
              <TBtn onClick={insertImage} title="Insert Image"> <ImagePlus  size={12}/></TBtn>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              {/* Undo/Redo */}
              <TBtn onClick={() => exec('undo')} title="Undo (Ctrl+Z)"><Undo size={12}/></TBtn>
              <TBtn onClick={() => exec('redo')} title="Redo (Ctrl+Y)"><Redo size={12}/></TBtn>

              <div className="w-px h-5 bg-[var(--border)] mx-1" />

              <TBtn onClick={() => exec('removeFormat')} title="Clear Formatting"><Type size={12}/></TBtn>
            </div>

            {/* Tip bar */}
            <div className="shrink-0 px-4 py-1.5 bg-[var(--bg-card)] border-b border-[var(--border)] flex items-center gap-1">
              <span className="text-[10px] text-[var(--text-tertiary)]">
                Click in the document to place your cursor · Use toolbar to format · Use the left panel to insert variables · Variables are fully editable text
              </span>
            </div>

            {/* A4 Canvas */}
            <div className="flex-1 overflow-auto bg-[var(--bg-app)] p-6">
              <div className="max-w-[760px] mx-auto">
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={saveCursor}
                  onKeyUp={saveCursor}
                  onMouseUp={saveCursor}
                  onFocus={saveCursor}
                  className="focus:outline-none"
                  style={{
                    minHeight: '1080px',
                    background: 'white',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    borderRadius: '10px',
                    padding: '72px 80px',
                    fontSize: '13px',
                    fontFamily: "'Georgia','Times New Roman',serif",
                    lineHeight: '1.85',
                    color: '#1e293b',
                    cursor: 'text',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
